/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Icon as DSIcon } from "@equipmentshare/ds-icon";
import {
  Either,
  Option,
  Ord,
  ReadonlyArray,
  Record,
  TaskEither,
} from "@morphism/fp";

import * as Ant from "antd";
import moment from "moment-timezone";
import React from "react";
import styled from "styled-components";

import { Asset } from "~/Asset";
import { Button } from "~/ButtonV2";
import { Collapse } from "~/Collapse";

import * as Color from "~/Color";
import {
  Comment as GenericComment,
  Comments as GenericComments,
} from "~/Comment";
import { DVIR } from "~/DVIR";
import * as ErrorMessage from "~/ErrorMessage";
import { Graph, gql } from "~/Graph";
import * as Icon from "~/Icon";
import { ID } from "~/ID";
import { Input } from "~/Input";
import * as Layout from "~/Layout";
import { Lifecycle } from "~/Lifecycle";
import { Media } from "~/Media";
import { NotApplicable } from "~/NotApplicable";
import { Remote } from "~/Remote";
import { Signature } from "~/Signature";
import { Time } from "~/Time";
import * as Typography from "~/Typography";
import { User } from "~/User";
import { WorkOrder } from "~/WorkOrder";

import { Images } from "./Image";

export type Defect = {
  readonly defect: ID &
    Lifecycle &
    Defect.Severity &
    Defect.Type &
    Partial<GenericComments & Defect.Resolution> &
    Graph.Errors.GraphErrors & {
      readonly asset: Asset.OptionalResult;
      readonly workOrder?: WorkOrder.Result;
      readonly images: Media.Images;
    };
};

export function Defect({ dvir, defect }: { readonly dvir: DVIR } & Defect) {
  const trailerAssets = pipe(
    dvir.trailers,
    ReadonlyArray.reduceRight(
      [],
      Either.reduceRight<Asset, readonly Asset[]>([], (trailer, previous) => [
        ...previous,
        trailer,
      ])
    )
  );

  const isTrailerDefect = trailerAssets.some(
    (trailer) =>
      Either.getOrElseW(() => undefined)(defect.asset)?.asset.id ===
      trailer.asset.id
  );

  React.useEffect(() => {
    if (defect.graphErrors && defect.graphErrors.length > 0) {
      pipe(
        defect.graphErrors,
        ReadonlyArray.map((error) =>
          Graph.Errors.displayMessage({
            graphError: Error(
              `We encountered and error while obtaining photos and/or Notes for the DVIR.  Error: ${error.message}`
            ),
          })
        )
      );
    }
  }, [defect.graphErrors]);

  return (
    <Defect.Container>
      {defect.id !== Defect.none(dvir).defect.id && defect.resolution && (
        <Layout.Block.Component bottom>
          Resolved with
          {defect.resolution.type === "CLOSED" && <>out</>}
          &nbsp;corrections
          {defect.resolution.submitted && (
            <>
              &nbsp;on&nbsp;
              {Time.DateTime.toDateOnly(defect.resolution.submitted)} at&nbsp;
              {Time.DateTime.toHHMM(defect.resolution.submitted)}
            </>
          )}
          {defect.resolution.signature?.submitted?.name && (
            <>&nbsp;by {defect.resolution.signature?.submitted?.name}</>
          )}
          .
        </Layout.Block.Component>
      )}
      <Images images={defect.images} />
      <Layout.Block.Component top bottom>
        <Defect.Details>
          <Defect.Detail title={isTrailerDefect ? "Trailer" : "Vehicle"}>
            {pipe(
              defect.asset,
              Either.map((asset) =>
                asset ? <Asset.Link {...asset} /> : <div>None</div>
              ),
              Either.getOrElse((error) => (
                <ErrorMessage.Component icon danger>
                  {error.message}
                </ErrorMessage.Component>
              ))
            )}
          </Defect.Detail>
          <Defect.Detail title="Work order">
            {pipe(
              Option.fromNullable(defect.workOrder),
              Option.map(
                Either.fold(
                  (error) => (
                    <ErrorMessage.Component icon danger>
                      {error.message}
                    </ErrorMessage.Component>
                  ),
                  (workOrder) => <WorkOrder.Link noDescription {...workOrder} />
                )
              ),
              Option.getOrElse(() => <NotApplicable />)
            )}
          </Defect.Detail>
          <Defect.Detail title="Severity">
            <Defect.Severity {...defect} />
          </Defect.Detail>
          <Defect.Detail title="Timestamp">
            <Layout.Flex.Component justify="center">
              <div>{Time.DateTime.toDateOnly(defect.lifecycle.submitted)}</div>
              <div>{Time.DateTime.toHMM(defect.lifecycle.submitted)}</div>
            </Layout.Flex.Component>
          </Defect.Detail>
        </Defect.Details>
      </Layout.Block.Component>
      <Defect.Comments dvir={dvir} defect={defect} />
      <Layout.Block.Component bottom>
        <Typography.H4.Component bottom>
          Driver signature
        </Typography.H4.Component>
        <Signature {...dvir} />
      </Layout.Block.Component>
      <Layout.Block.Component>
        <Defect.ResolvedBySignature defect={defect} />
      </Layout.Block.Component>
    </Defect.Container>
  );
}

export namespace Defect {
  export const orderBySubmittedDate = Ord.fromCompare<Defect>((a, b) =>
    pipe(
      [a, b],
      ReadonlyArray.map((d) => d.defect.lifecycle.submitted.dateTime),
      ([a, b]) => Ord.ordNumber.compare(a, b)
    )
  );

  export type Fragment = Graph.Generated.DefectFragment;
  export namespace Fragment {
    export const document = () => gql`
      ${Asset.Result.Fragment.document()}
      ${Resolution.Fragment.document()}
      ${Lifecycle.Submitted.Fragment.document()}
      ${GenericComments.Fragment.document()}
      ${Media.Fragment.document()}
      ${WorkOrder.Result.Fragment.document()}

      fragment Defect on Asset__Defect {
        id
        severity
        type {
          id
          name
        }
        asset {
          ...Asset__Result
        }
        resolution {
          ...Resolution
        }
        workOrders {
          ...WorkOrder__Result
        }
        ...Submitted
        ...Comments
        ...Media
      }
    `;

    export const decode = (encoded: Fragment): Defect => {
      const decoded = {
        defect: {
          id: encoded.id,
          lifecycle: encoded.lifecycle,
          asset: Asset.Result.Fragment.decode(encoded.asset),
          severity: encoded.severity,
          ...(encoded.resolution &&
            Resolution.Fragment.decode(encoded.resolution)),
          type: encoded.type,
          workOrder: pipe(
            Option.fromNullable(encoded.workOrders?.[0]),
            Option.map(WorkOrder.Result.Fragment.decode),
            Option.toUndefined
          ),

          ...GenericComments.Fragment.decode(encoded),
          ...Media.Image.decode(encoded as Media.Fragment),
        },
      };

      return decoded
    };
  }

  export const none = (
    dvir: DVIR,
    asset: Asset.OptionalResult = Either.right(undefined)
  ): Defect => {
    const assetCommentsAndMedia = AssetCommentsAndMedia.forAsset(dvir, asset);
    return {
      defect: {
        id: "none",
        lifecycle: dvir.lifecycle,
        resolution: { type: "N/A" },
        severity: "N/A",
        type: { id: "no-defects", name: "No defects" },
        asset,
        comments: assetCommentsAndMedia.reduce<readonly GenericComment[]>(
          (acc, acm) => [...acc, ...acm.comments],
          []
        ),
        images: assetCommentsAndMedia.reduce<Media.Images>(
          (acc, acm) => [...acc, ...acm.images],
          []
        ),
        graphErrors: [
          ...AssetCommentsAndMedia.toErrors(dvir),
          ...(Either.isLeft(asset) ? [asset.left] : []),
        ],
      },
    };
  };

  export namespace AssetCommentsAndMedia {
    export const toData = (
      assetCommentsAndMedia: readonly Either.ErrorOr<
        DVIR.AssetCommentsAndMedia
      >[]
    ): readonly DVIR.AssetCommentsAndMedia[] =>
      pipe(assetCommentsAndMedia, ReadonlyArray.filterMap(Option.fromEither));

    export const toErrors = ({
      assetCommentsAndMedia,
    }: DVIR): readonly Error[] =>
      pipe(
        assetCommentsAndMedia,
        ReadonlyArray.filterMap((acm) =>
          Either.isLeft(acm) ? Option.some(acm.left) : Option.none
        )
      );

    export const forAsset = (
      { assetCommentsAndMedia }: DVIR,
      asset: Asset.OptionalResult
    ) => {
      const dvirAsset = optionalAsset(asset);
      return dvirAsset
        ? pipe(
            toData(assetCommentsAndMedia),
            ReadonlyArray.filterMap((acm) => {
              const acmAsset = optionalAsset(acm.asset);
              return acmAsset && acmAsset.asset.id === dvirAsset.asset.id
                ? Option.some(acm)
                : Option.none;
            })
          )
        : [];
    };

    export const optionalAsset = (
      asset: Asset.OptionalResult
    ): Asset | undefined => Either.getOrElseW(() => undefined)(asset);
  }

  export const addSpecialNoDefects = (
    dvir: DVIR,
    asset: Asset.OptionalResult,
    defects: readonly Defect[]
  ) => {
    const noDefectDefect = Defect.none(dvir, asset);
    if (
      noDefectDefect.defect.images.length > 0 ||
      (noDefectDefect.defect.comments &&
        noDefectDefect.defect.comments?.length > 0)
    ) {
      return [...defects, noDefectDefect];
    }
    return defects;
  };

  export const Container = styled.div``;

  export const Details = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `;

  export function Detail({
    title,
    children,
  }: React.PropsWithChildren<{ readonly title: string }>) {
    return (
      <Detail.Container>
        <Typography.H4.Component>{title}</Typography.H4.Component>
        <div>{children}</div>
      </Detail.Container>
    );
  }

  export namespace Detail {
    export const Container = styled.div`
      display: flex;
      flex-direction: column;
      flex-basis: 25%;
      align-items: center;

      & > div > a {
        display: inline;
      }
    `;
  }

  export type Severity = {
    readonly severity: "OPERATIONAL" | "SOFT_DOWN" | "HARD_DOWN" | "N/A";
  };

  export function Severity({ severity }: Severity) {
    return severity === "OPERATIONAL" ? (
      <NotApplicable />
    ) : severity === "N/A" ? (
      <NotApplicable />
    ) : (
      <>
        {severity.charAt(0)}
        {severity.slice(1).toLocaleLowerCase().replace("_", " ")}
      </>
    );
  }

  export type Type = { readonly type: ID & { readonly name: string } };

  export type Resolution = {
    readonly resolution: Partial<Signature & Lifecycle.Submitted> & {
      readonly type: "CLOSED" | "RESOLVED" | "N/A";
    };
  };

  export function Resolution({
    resolution,
    loading,
    onClick,
  }: Partial<Resolution> & {
    readonly loading: boolean;
    readonly onClick: () => void;
  }) {
    const spacing = <>&nbsp;&nbsp;</>;
    const props: React.ComponentProps<typeof Button> = loading
      ? {
          loading: true,
          icon: null,
          children: <>{spacing}Resolving issue</>,
        }
      : {
          icon: <Icon.CheckCircle />,
          children: <>{spacing}Resolve issue</>,
          onClick: (e) => {
            e.stopPropagation();
            onClick();
          },
        };

    const resolutionStatus = (resolution ?? { type: "RESOLVED" }).type;
    return (
      <Resolution.Container>
        {resolution ? (
          <>
            <Layout.Inline.Component style={{ display: "flex" }} right>
              <DSIcon
                name="$icon_check_circle_fill"
                color="$color_primary_green"
              />
            </Layout.Inline.Component>
            {resolutionStatus.charAt(0) +
              resolutionStatus.slice(1).toLocaleLowerCase()}
          </>
        ) : (
          <Button type="primary" {...props} />
        )}
      </Resolution.Container>
    );
  }

  export namespace Resolution {
    export type Fragment = Graph.Generated.ResolutionFragment;
    export namespace Fragment {
      export const document = () => gql`
        ${Lifecycle.Submitted.Fragment.document()}
        ${Signature.Fragment.document()}
        ${GenericComments.Fragment.document()}

        fragment Resolution on Asset__Defect__Resolution {
          id
          type
          ...Submitted
          ...Signature
          ...Comments
        }
      `;

      export const decode = (encoded: Fragment): Resolution => ({
        resolution: {
          type: encoded.type,
          ...Signature.decode(encoded),
          ...Lifecycle.Submitted.decode(encoded),
        },
      });
    }

    export function Card({
      loading = false,
      resolution,
      name,
      setResolution,
      setName,
      onResolve,
    }: Card.Props) {
      return (
        <Card.Container>
          <Card.RadioContainer onClick={() => setResolution("RESOLVED")}>
            <Input
              style={{ width: "auto", marginRight: "0.5em" }}
              type="radio"
              checked={resolution === "RESOLVED"}
            />
            Defect corrected
          </Card.RadioContainer>
          <Card.RadioContainer onClick={() => setResolution("CLOSED")}>
            <Input
              style={{ width: "auto", marginRight: "0.5em" }}
              type="radio"
              checked={resolution === "CLOSED"}
            />
            Defect does not need correction
          </Card.RadioContainer>
          <div>
            {
              "By signing below, I certify that to the best of my knowledge this vehicle has been inspected with defects present corrected on "
            }
            {Time.DateTime.toDateOnlyMDYY(Time.DateTime.now())}.
          </div>
          <Input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Type signature here..."
          />
          <Button
            style={{ alignSelf: "flex-end" }}
            type="primary"
            ghost
            disabled={!resolution || name.trim() === "" || loading}
            onClick={onResolve}
          >
            Approve
          </Button>
        </Card.Container>
      );
    }

    export namespace Card {
      export type Props = {
        readonly loading?: boolean;
        readonly resolution: "RESOLVED" | "CLOSED" | undefined;
        readonly name: string;
        readonly setResolution: (resolution: "RESOLVED" | "CLOSED") => void;
        readonly setName: (name: string) => void;
        readonly onResolve: () => void;
      };
      export const Container = styled.div`
        display: flex;
        flex-direction: column;

        & > * {
          margin-bottom: 15px;
        }
      `;

      export const RadioContainer = styled.div`
        display: flex;
        flex-direction: row;
        align-items: center;
        padding-left: 20px;
      `;
    }

    export namespace Mutate {
      export type ResolveVariables = {
        readonly defectID: string;
        readonly resolution: "RESOLVED" | "CLOSED";
        readonly signatureID: string;
      } & Lifecycle.Submitted;

      export const useResolve = () => {
        const [mutate, ...rest] = Graph.useMutation(
          gql`
            ${Defect.Resolution.Fragment.document()}
            ${Graph.Errors.Fragment.document()}

            mutation ResolveDefect(
              $defectID: ID!
              $resolution: Asset__Defect__Resolution__Type!
              $signatureID: ID!
              $lifecycle: Lifecycle__CreateInput!
            ) {
              driver {
                vehicleInspectionReport {
                  defects {
                    resolve(
                      input: {
                        assetDefectID: $defectID
                        assetDefectResolutionType: $resolution
                        signatureID: $signatureID
                        lifecycle: $lifecycle
                      }
                    ) {
                      ...Resolution
                      ...Error
                    }
                  }
                }
              }
            }
          `
        );

        return [
          ({
            defectID,
            resolution,
            signatureID,
            submitted,
          }: ResolveVariables) => {
            const lifecycle = {
              submitted: {
                date: submitted.dateTime,
                actor: {
                  actorType: "HUMAN",
                  actorName: submitted.name,
                  userID: submitted.user?.id,
                },
              },
            };

            return TaskEither.fromTask(() =>
              mutate({
                variables: {
                  defectID,
                  resolution,
                  signatureID,
                  lifecycle,
                },
                update(cache, { data }) {
                  // shortcut out of this flow if a graph error is found
                  if (data.driver.vehicleInspectionReport) {
                    const decodedErrFrag = Graph.Errors.Fragment.decode(data.driver.vehicleInspectionReport.defects.resolve[0] as Graph.Errors.Fragment);
                    if (Graph.Errors.isGraphError(decodedErrFrag)) {
                      return TaskEither.left(decodedErrFrag);
                    }
                  }
                  cache.writeFragment({
                    id: `Asset__Defect:${defectID}`,
                    fragmentName: "ResolvedAssetDefect",
                    fragment: gql`
                      ${Defect.Resolution.Fragment.document()}

                      fragment ResolvedAssetDefect on Asset__Defect {
                        resolution {
                          ...Resolution
                        }
                      }
                    `,
                    data: {
                      resolution:
                        data?.driver?.vehicleInspectionReport?.defects
                          ?.resolve[0],
                    },
                  });
                },
              })
            );
          },
          ...rest,
        ] as const;
      };
    }

    export const Container = styled.div`
      position: absolute;
      top: 0;
      right: 16px;
      bottom: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    `;
  }

  export const ResolvedBySignature = ({ defect }: Defect) => (
    <>
      <Typography.H4.Component bottom>
        Mechanic signature
      </Typography.H4.Component>
      <Signature {...defect.resolution} />
    </>
  );

  export function Comment({ comment: { text, submitted } }: GenericComment) {
    return text ? (
      <Comment.Container>
        <BorderedBox>
          <div>
            <Typography.Component muted>
              {submitted.name && <>{submitted.name} added on </>}
              {Time.DateTime.toDateOnlyMDYY(submitted)}:
            </Typography.Component>
          </div>
          {text}
        </BorderedBox>
      </Comment.Container>
    ) : null;
  }

  export namespace Comment {
    export const Container = styled.div`
      margin-bottom: ${Layout.Inline.padding()};

      &:last-of-type {
        margin-bottom: 0;
      }

      & > div {
        margin-bottom: ${Layout.Inline.padding()};
      }
    `;

    export function Create({ defect }: Defect) {
      const [comment, setComment] = React.useState("");
      const [create, result] = Create.use({
        defect,
        text: comment,
        onSuccess: () => setComment(""),
        onError: () => Ant.message.error("Something went wrong!"),
      });

      const remote = Graph.Response.toRemote(result);
      const spacing = <>&nbsp;&nbsp;</>;
      const props: React.ComponentProps<typeof Button> = Remote.isLoading(
        remote
      )
        ? {
            loading: true,
            icon: null,
            children: <>{spacing}Adding note</>,
          }
        : {
            icon: <Icon.PlusCircle />,
            children: <>{spacing}Save note</>,
            onClick: () => create(),
          };

      return (
        <Create.Container>
          <Create.Input
            disabled={Remote.isLoading(remote)}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <div>
            <Button type="link" {...props} />
          </div>
        </Create.Container>
      );
    }

    export type Create = Defect & {
      readonly text: string;
      readonly onSuccess: () => void;
      readonly onError: () => void;
    };

    export namespace Create {
      export const use = (comment: Create) => Mutation.use(comment);

      export namespace Mutation {
        export const document = () => gql`
          mutation DVIRCreateComment(
            $defectID: ID!
            $dateTime: DateTime!
            $userID: ID!
            $userName: String
            $text: String!
          ) {
            vehicleInspectionReport {
              defects {
                update(
                  input: {
                    id: $defectID
                    comments: {
                      text: $text
                      lifecycle: {
                        submitted: {
                          date: $dateTime
                          actor: {
                            actorType: USER
                            actorName: $userName
                            userID: $userID
                          }
                        }
                      }
                    }
                  }
                ) {
                  ... on Asset__Defect {
                    id
                    ...Comments
                  }
                }
              }
            }
          }

          ${GenericComments.Fragment.document()}
        `;

        export const use = ({ defect, text, onError, onSuccess }: Create) =>
          pipe(User.use(), (user) =>
            Graph.useMutation<
              Graph.Generated.DvirCreateCommentMutation,
              Graph.Generated.DvirCreateCommentMutationVariables
            >(document(), {
              onError,
              update: onSuccess,
              variables: {
                defectID: defect.id,
                dateTime: Time.DateTime.toISO(Time.DateTime.now()),
                userID: user.id,
                userName: pipe(
                  Remote.toUndefined(user)?.user?.name,
                  (name) => `${name?.first}${name?.last ? ` ${name.last}` : ""}`
                ),
                text,
              },
            })
          );
      }

      export const Container = styled.div`
        & > div:last-of-type {
          display: flex;
          justify-content: flex-end;
        }
      `;

      export const Input = (props: React.ComponentProps<typeof Ant.Input>) => (
        <Ant.Input placeholder="Start typing your note here..." {...props} />
      );
    }
  }

  export function Comments({
    dvir,
    defect,
    print,
  }: { readonly dvir: DVIR; readonly print?: boolean } & Defect) {
    const none = (
      <BorderedBox>
        <Typography.Component muted>No notes</Typography.Component>
      </BorderedBox>
    );

    return pipe(
      defect.comments ?? [],
      ReadonlyArray.partition(
        ({ comment }) =>
          comment.submitted.user?.id !==
          Either.getOrElseW(() => undefined)(dvir.driver)?.id
      ),
      ({ left, right }) => [left, right],
      ReadonlyArray.map(
        ReadonlyArray.mapWithIndex((index, comment) => (
          <Comment key={index} {...comment} />
        ))
      ),
      ([driverComments, additionalComments]) => (
        <>
          <Layout.Block.Component bottom>
            <Typography.H4.Component bottom>
              Driver notes
            </Typography.H4.Component>
            {driverComments[0] ? driverComments : none}
          </Layout.Block.Component>
          <Layout.Block.Component bottom>
            <Typography.H4.Component bottom>
              Additional notes
            </Typography.H4.Component>
            <Comments.Container>
              {additionalComments}
              {print && !additionalComments[0] && none}
              {!print && <Comment.Create defect={defect} />}
            </Comments.Container>
          </Layout.Block.Component>
        </>
      )
    );
  }

  export namespace Comments {
    export const Container = styled.div`
      display: flex;
      flex-direction: column;
      & > div {
        margin-bottom: ${() => Layout.Inline.padding()};
        &:last-of-type {
          margin-bottom: 0;
        }
      }
    `;
  }

  export const BorderedBox = styled.div`
    background: ${() => Color.almostWhite()};
    padding: ${() => Layout.Inline.padding()};
    border-radius: ${() => Layout.Inline.padding()};
    border: 1px solid ${() => Color.veryWhiteGrey()};
  `;
}

export type Defects = { readonly defects: readonly Defect[] };
export function Defects({ dvir }: { readonly dvir: DVIR }) {
  type OpenDefects = { readonly [id: string]: boolean };
  const [openDefects, setOpenDefects] = React.useState(
    pipe(
      dvir.defects,
      ReadonlyArray.reduce(
        {} as OpenDefects,
        (openDefects, { defect: { id, resolution } }) => ({
          ...openDefects,
          [id]: resolution === undefined,
        })
      )
    )
  );

  const toggleOpen = ({ defect: { id } }: Defect) =>
    setOpenDefects({ ...openDefects, [id]: !(openDefects[id] ?? false) });

  toggleOpen;

  const hasDefects = !!dvir.defects[0];
  const activeKey = !hasDefects
    ? []
    : pipe(
        openDefects,
        Record.reduceWithIndex([] as readonly string[], (key, keys, isOpen) =>
          isOpen ? [...keys, key] : keys
        )
      );

  const vehicle = Either.getOrElseW(() => undefined)(dvir.vehicle)?.asset;

  const vehicleDefects = pipe(
    dvir.defects,
    ReadonlyArray.filter(
      ({ defect: { asset } }) =>
        Either.getOrElseW(() => undefined)(asset)?.asset.id === vehicle?.id
    ),
    Option.fromNullable,
    Option.fold(
      () => (!vehicle ? [] : [Defect.none(dvir, dvir.vehicle)]),
      (defects) =>
        defects.length > 0
          ? Defect.addSpecialNoDefects(dvir, dvir.vehicle, defects)
          : !vehicle
          ? []
          : [Defect.none(dvir, dvir.vehicle)]
    ),
    ReadonlyArray.map(({ defect }) => ({
      defect: { source: "vehicle", ...defect },
    }))
  );

  const trailersDefects = dvir.trailers
    .map((trailerOrError) => {
      const trailer = Either.getOrElseW(() => undefined)(trailerOrError);
      const trailerDefects = dvir.defects.filter(
        ({ defect: { asset } }) =>
          Either.getOrElseW(() => undefined)(asset)?.asset.id ===
          trailer?.asset?.id
      );
      return trailer && trailerDefects.length < 1
        ? [Defect.none(dvir, trailerOrError)]
        : Defect.addSpecialNoDefects(dvir, trailerOrError, trailerDefects);
    })
    .reduce<readonly Defect[]>(
      (defectAccumulation, defects) => [...defectAccumulation, ...defects],
      []
    )
    .map(({ defect }) => ({ defect: { source: "trailer", ...defect } }));

  type Panels = { readonly [assetID: string]: readonly JSX.Element[] };
  const panels: Panels = pipe(
    [...vehicleDefects, ...trailersDefects],
    ReadonlyArray.sortBy([Defect.orderBySubmittedDate]),
    ReadonlyArray.reduce({} as Panels, (previous, { defect }) => {
      const [show, setShow] = React.useState(false);
      const [loading, setLoading] = React.useState(false);
      const [signerName, setSignerName] = React.useState("");
      const [resolution, setResolution] = React.useState<
        "CLOSED" | "RESOLVED" | undefined
      >(undefined);

      const [createSignature] = Signature.Mutate.create({
        signature: {
          submitted: { dateTime: moment.now(), name: signerName },
        },
      });
      const [resolveDefect] = Defect.Resolution.Mutate.useResolve();

      const userInfo = User.use();

      const resolve = pipe(
        createSignature,
        TaskEither.chain(({ data }) => {
          if (data?.signatures?.create && "id" in data?.signatures?.create) {
            return TaskEither.right(data?.signatures?.create.id);
          }
          return TaskEither.left(Error("Something went wrong!"));
        }),
        TaskEither.chain((signatureID) =>
          resolveDefect({
            defectID: defect.id,
            resolution: resolution || "CLOSED",
            signatureID: signatureID,
            submitted: {
              dateTime: moment.now(),
              user: { id: userInfo.id },
              name: pipe(Remote.toUndefined(userInfo)?.user?.name, (name) =>
                [name?.first, name?.last].filter((s) => s).join(" ")
              ),
            },
          })
        ),
        // TODO: figure out how to make the error detection from the resolveDefect 
        // mutation ensures a graph error gets caught and handled correctly in the left path
        TaskEither.chain((data) => {
          const { data: parsed } = data;
          if ("message" in parsed.driver.vehicleInspectionReport.defects.resolve[0]) {
            const decodedErrFrag = Graph.Errors.Fragment.decode(parsed.driver.vehicleInspectionReport.defects.resolve[0] as Graph.Errors.Fragment);
            
            if (Graph.Errors.isGraphError(decodedErrFrag)) {
              return TaskEither.left(decodedErrFrag)
            }
          }
          return TaskEither.right(data);
        }),
        TaskEither.bimap(
          () => {
            setLoading(false);
            void Ant.message.error("Something went wrong! Please verify any outstanding work orders associated with this vehicle are completed.");
          },
          () => {
            setLoading(false);
            void Ant.message.success("Defect resolved!");
            setResolution(undefined);
            setSignerName("");
            setShow(false);
          }
        ),
      );

      const defectAssetID = pipe(
        Either.getOrElseW(() => undefined)(defect.asset),
        (assetOrUndefined) =>
          !assetOrUndefined ? "none" : assetOrUndefined.asset?.id,
        (id) => (id === "none" ? defect.source : id)
      );

      return !defectAssetID
        ? previous
        : {
            ...previous,
            [defectAssetID]: [
              ...(previous[defectAssetID] ?? []),
              <Collapse.Panel
                key={defect.id}
                header={defect.type.name}
                extra={
                  defect.type.id !== "no-defects" && (
                    <Defect.Resolution
                      {...defect}
                      loading={loading}
                      onClick={() => setShow(true)}
                    />
                  )
                }
              >
                {show && (
                  <Defect.Resolution.Card
                    loading={loading}
                    name={signerName}
                    setName={setSignerName}
                    resolution={resolution}
                    setResolution={setResolution}
                    onResolve={() => {
                      setLoading(true);
                      void resolve();
                    }}
                  />
                )}
                <Defect dvir={dvir} defect={defect} />
              </Collapse.Panel>,
            ],
          };
    })
  );

  // TODO: Refactor, this got really confusing with resolution code mixed-in
  return pipe(
    panels,
    Record.partitionMapWithIndex((assetID, panels) =>
      pipe(
        <div data-testid={`collapsible-${assetID}`} key={assetID}>
          <Defects.Header
            isVehicle={assetID === vehicle?.id || assetID === "vehicle"}
            name={pipe(
              Either.getOrElseW(() => undefined)(dvir?.vehicle) !== undefined
                ? [dvir.vehicle as Asset.Result]
                : [],
              (vehicleAssets) => [...vehicleAssets, ...dvir.trailers],
              ReadonlyArray.rights,
              ReadonlyArray.findFirst(({ asset: { id } }) => id === assetID),
              Option.map(Asset.name),
              Option.toUndefined
            )}
          />
          <Collapse defaultActiveKey={activeKey as never}>{panels}</Collapse>
        </div>,
        (collapse) =>
          assetID === vehicle?.id || assetID === "vehicle"
            ? Either.left(collapse)
            : Either.right(collapse)
      )
    ),
    ({ left: vehicles, right: trailers }) => (
      <>
        {pipe(Object.values(vehicles), ([first, ...rest]) =>
          first ? [first, ...rest] : <Defects.Header isEmpty isVehicle={true} />
        )}
        {pipe(Object.values(trailers), ([first, ...rest]) =>
          first ? (
            [first, ...rest]
          ) : (
            <Defects.Header isEmpty isVehicle={false} />
          )
        )}
      </>
    )
  );
}

export namespace Defects {
  export type Fragment = Graph.Generated.DefectsFragment;
  export namespace Fragment {
    export const document = () => gql`
      ${Defect.Fragment.document()}

      fragment Defects on Asset__Defect__HasMany {
        defects {
          ...Defect
        }
      }
    `;

    export const decode = (original: Fragment): Defects => {
      return {
        defects: pipe(
          original.defects ?? [],
          ReadonlyArray.filterMap(
            flow(Option.fromNullable, Option.map(Defect.Fragment.decode))
          )
        ),
      };
    };
  }

  export function Header({ isVehicle, isEmpty = false, name }: Header.Props) {
    return (
      <Header.Container isEmpty={isEmpty}>
        <div />
        <div>
          &nbsp;
          {isVehicle ? "VEHICLE" : "TRAILER"}
          {isEmpty && !isVehicle ? "S" : ""}
          {(isEmpty || name) && (
            <>
              : <span>{name ?? "None"}</span>
            </>
          )}
        </div>
      </Header.Container>
    );
  }

  export namespace Header {
    export type Props = {
      readonly isVehicle?: boolean;
      readonly isEmpty?: boolean;
      readonly name?: string;
    };

    export const Container = styled.div<Props>`
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: ${() => Layout.Block.padding()};
      padding-bottom: ${() => Layout.Inline.padding()};
      color: ${({ isEmpty }) =>
        isEmpty ? Typography.Muted.color() : Color.black()};

      & > div:first-of-type {
        flex-grow: 1;
        height: 1px;
        background: ${({ isEmpty }) =>
          isEmpty ? Typography.Muted.color() : Color.black()};
      }

      & > div:last-of-type {
        & > span {
          font-weight: bold;
        }
      }
    `;
  }
}
